import { useState } from 'react';
import { listContacts } from 'services/Contact/contactServices';
import { totalCountEvent } from 'services/generalServices';
import { getFullName } from 'utils/utils';

export function sortContactsForDisplay(contacts) {
  if (!Array.isArray(contacts)) return [];
  return [...contacts].sort((a, b) => {
    const label = (c) => {
      const fromPerson = getFullName(c?.first_name, c?.middle_name, c?.last_name)?.trim();
      if (fromPerson) return fromPerson.toLowerCase();
      const t = (c?.title || c?.business_name || '').trim();
      if (t) return t.toLowerCase();
      return String(c?.contact_id ?? c?.sk ?? '').toLowerCase();
    };
    return label(a).localeCompare(label(b));
  });
}

/**
 * Hook to search and load referral contacts.
 * @param {boolean} isLead - Filter contacts linked to leads.
 * @param {string} contactType - Contact type filter (e.g. 'person').
 * @param {boolean} isReferralEntity - Filter to referral entities only.
 * @param {number} defaultPageLimit - Page size for normal search (type-ahead).
 * @param {boolean} businessPersonOnly - Restrict to business/person contacts.
 * @param {boolean} fetchAll - When true, fetches the total count first then loads
 *   the entire contact list in one request (use for report filter dropdowns).
 * @param {boolean} explicitContactFilters - When true, list-contacts always receives
 *   contact_type (including empty), in_lead, and is_referral_entity query params.
 */
export function useReferredToContact(
  isLead = false,
  contactType = '',
  isReferralEntity = false,
  defaultPageLimit = 15,
  businessPersonOnly = false,
  fetchAll = false,
  explicitContactFilters = false
) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  /**
   * @param {object} e - Search payload, e.g. `{ query: string }`
   * @param {string} [pagination_token]
   * @param {string} [pagination_direction]
   * @param {number} [pageLimit]
   * @param {{ quiet?: boolean }} [options] - When `quiet: true`, skip global loading state (e.g. scroll pagination)
   */
  const searchAsync = async (e, pagination_token = '', pagination_direction = 'forward', pageLimit = defaultPageLimit, options = {}) => {
    const quiet = Boolean(options?.quiet);
    try {
      if (!quiet) setLoading(true);

      let effectivePageLimit = pageLimit;
      if (fetchAll) {
        const countResponse = await totalCountEvent('contact');
        effectivePageLimit = countResponse?.data || pageLimit;
      }

      const response = await listContacts({
        contactType,
        keyWord: e?.query,
        pageLimit: effectivePageLimit,
        excludeFromLoading: true,
        in_lead: isLead,
        is_referral_entity: isReferralEntity,
        pagination_token,
        pagination_direction,
        bussiness_person_only: businessPersonOnly,
        explicitContactFilters,
      });

      const contacts = response?.data?.contacts ?? [];
      const sortedContacts = sortContactsForDisplay(contacts);
      if (!quiet) {
        setResults(sortedContacts);
      }
      if (!quiet) setLoading(false);

      // Return full response data including pagination info
      return {
        contacts: sortedContacts,
        next_token: response?.data?.next_token || '',
        transition_token: response?.data?.transition_token || '',
      };
    } catch (error) {
      console.error('Error fetching data:', error);
      if (!quiet) setLoading(false);
      return { contacts: [], next_token: '', transition_token: '' };
    }
  };

  return { contactList: results, searchAsync, loading, setResults };
}
